import { useQuery } from '@tanstack/react-query';
import { MonitorCog } from 'lucide-react';
import { CodeBlock } from '@/components/ui/code-block';
import { Skeleton } from '@/components/ui/skeleton';
import { createHyperVInstallerLink, hyperVInstallCommand } from '@/features/gateway-deploy';

interface Props {
  /** The provisioning token the gateway claims the organization with. */
  token: string;
}

/**
 * Hyper-V install: one PowerShell command, run as Administrator on the Windows
 * host, downloads the gateway, creates and starts its VM, and claims it with
 * the token. The command carries a signed link to the image, made for this
 * user when the dialog opens; nothing about it is stored.
 */
export function HyperVInstallInstructions({ token }: Props) {
  const { data: link, isLoading } = useQuery({
    queryKey: ['hyperv-installer-link', token],
    queryFn: createHyperVInstallerLink,
    staleTime: Infinity,
    retry: false,
  });

  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (!link) return null;

  return (
    <div className="rounded-md border border-border-subtle p-4">
      <div className="flex items-start gap-3 mb-3">
        <MonitorCog className="w-8 h-8 text-sev-5 shrink-0" />
        <div>
          <h4 className="font-semibold">Hyper-V (Windows)</h4>
          <p className="text-sm text-muted-foreground">
            On the Windows host, open PowerShell as Administrator and run this. It downloads the
            gateway, creates and starts a Hyper-V virtual machine, and claims it for your
            organization.
          </p>
        </div>
      </div>
      <CodeBlock code={hyperVInstallCommand(link, token)} />
      <p className="mt-2 text-xs text-muted-foreground">
        The command works for 3 days. It connects the gateway to the host&apos;s external virtual
        switch, and asks before creating one if there is none.
      </p>
    </div>
  );
}
